import type { ReactElement } from 'react'
import { CreateRelationshipModal } from '../CreateRelationshipModal'
import { ViewAllLinksModal } from '../../../../components/infrastructure/ViewAllLinksModal'
import { EditLinkModal } from '../../../../components/infrastructure/EditLinkModal'
import { BulkCreateLinkModal } from '../../../../components/infrastructure/BulkCreateLinkModal'
import { CopyLinksFromModal } from '../../../../components/infrastructure/CopyLinksFromModal'
import type { CaseInfrastructureTabModalsProps } from '../CaseInfrastructureTabModals'

type CreateBulkRelationshipModalsProps = Pick<
  CaseInfrastructureTabModalsProps,
  | 'endpoints'
  | 'questionTemplates'
  | 'selectedIds'
  | 'creatingRelationship'
  | 'relationshipError'
  | 'linkingEndpoint'
  | 'setLinkingEndpoint'
  | 'bulkLinkSourceEndpoint'
  | 'setBulkLinkSourceEndpoint'
  | 'handleCreateRelationship'
  | 'handleBulkCreateRelationships'
  | 'clearRelationshipError'
>

export function CreateBulkRelationshipModals(
  props: CreateBulkRelationshipModalsProps
): ReactElement {
  const {
    endpoints,
    questionTemplates,
    selectedIds,
    creatingRelationship,
    relationshipError,
    linkingEndpoint,
    setLinkingEndpoint,
    bulkLinkSourceEndpoint,
    setBulkLinkSourceEndpoint,
    handleCreateRelationship,
    handleBulkCreateRelationships,
    clearRelationshipError,
  } = props
  return (
    <>
      {linkingEndpoint && (
        <CreateRelationshipModal
          sourceEndpoint={linkingEndpoint}
          endpoints={endpoints}
          templates={questionTemplates}
          onClose={() => {
            setLinkingEndpoint(null)
            clearRelationshipError()
          }}
          onCreate={(...args: Parameters<typeof handleCreateRelationship>) => {
            void handleCreateRelationship(...args)
          }}
          creating={creatingRelationship}
          error={relationshipError}
        />
      )}
      {bulkLinkSourceEndpoint && (
        <BulkCreateLinkModal
          sourceEndpoint={bulkLinkSourceEndpoint}
          targetIds={Array.from(selectedIds)}
          endpoints={endpoints}
          templates={questionTemplates}
          onClose={() => {
            setBulkLinkSourceEndpoint(null)
          }}
          onCreate={handleBulkCreateRelationships}
          creating={false}
          error={null}
        />
      )}
    </>
  )
}

type ViewEditRelationshipModalsProps = Pick<
  CaseInfrastructureTabModalsProps,
  | 'endpoints'
  | 'relationships'
  | 'questionTemplates'
  | 'deletingRelationship'
  | 'movingRelationship'
  | 'copyingLinks'
  | 'copyError'
  | 'viewLinksEndpoint'
  | 'setViewLinksEndpoint'
  | 'editingRelationship'
  | 'setEditingRelationship'
  | 'copyLinksTargetEndpoint'
  | 'setCopyLinksTargetEndpoint'
  | 'setCopyError'
  | 'handleUpdateRelationship'
  | 'handleDeleteRelationship'
  | 'handleCopyLinks'
  | 'handleMoveRelationship'
>

export function ViewEditRelationshipModals(props: ViewEditRelationshipModalsProps): ReactElement {
  const {
    endpoints,
    relationships,
    questionTemplates,
    deletingRelationship,
    movingRelationship,
    copyingLinks,
    copyError,
    viewLinksEndpoint,
    setViewLinksEndpoint,
    editingRelationship,
    setEditingRelationship,
    copyLinksTargetEndpoint,
    setCopyLinksTargetEndpoint,
    setCopyError,
    handleUpdateRelationship,
    handleDeleteRelationship,
    handleCopyLinks,
    handleMoveRelationship,
  } = props
  return (
    <>
      {viewLinksEndpoint && (
        <ViewAllLinksModal
          endpoint={viewLinksEndpoint}
          relationships={relationships}
          endpoints={endpoints}
          onClose={() => {
            setViewLinksEndpoint(null)
          }}
          onEdit={(relationship) => {
            setViewLinksEndpoint(null)
            setEditingRelationship(relationship)
          }}
          onDelete={(...args: Parameters<typeof handleDeleteRelationship>) => {
            void handleDeleteRelationship(...args)
          }}
          deleting={deletingRelationship}
        />
      )}
      {editingRelationship && (
        <EditLinkModal
          relationship={editingRelationship}
          endpoints={endpoints}
          templates={questionTemplates}
          onClose={() => {
            setEditingRelationship(null)
          }}
          onUpdate={handleUpdateRelationship}
          updating={false}
          error={null}
          onMove={(request) => handleMoveRelationship(editingRelationship.id, request)}
          moving={movingRelationship}
        />
      )}
      {copyLinksTargetEndpoint && (
        <CopyLinksFromModal
          targetEndpoint={copyLinksTargetEndpoint}
          endpoints={endpoints}
          relationships={relationships}
          onClose={() => {
            setCopyLinksTargetEndpoint(null)
            setCopyError(null)
          }}
          onCopy={handleCopyLinks}
          copying={copyingLinks}
          error={copyError}
        />
      )}
    </>
  )
}
